import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import {
  StripeCustomer,
  StripePlan,
  StripeProduct,
} from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import {
  authStore,
  checkoutStore,
  customerStore,
  productStore,
} from '../../../stores/global';
import type { PriceInterval } from '../types';

type PlanCardProps = {
  product: StripeProduct | null;
  interval: PriceInterval;
  highlighted?: boolean;
};

export default function PlanCard({
  product,
  interval,
  highlighted = false,
}: PlanCardProps) {
  const navigate = useNavigate();
  const { authStatus } = useSnapshot(authStore.state);
  const { customer } = useSnapshot(customerStore.state);
  const price = productStore.getPriceByProductAndMode(product?.id, interval);
  const subscriberPremiumStatus = customerStore.getSubscriberPremiumStatus(
    customer as any as StripeCustomer,
  );
  const isPaying = subscriberPremiumStatus === 'isPaying';

  // Determine if customer is on unlimited plan by checking their plans
  const isUnlimitedCustomer =
    isPaying &&
    customer?.plans?.some(
      (plan: StripePlan) => plan.product === getStripeProductID('advanced'),
    );

  // Determine the current plan based on subscription status
  const currentPlan = isUnlimitedCustomer
    ? 'unlimited'
    : isPaying
      ? 'pro'
      : 'free';

  const cta = getCTA(authStatus, currentPlan, product);

  const isProPlan = product?.name.includes('Pro');
  const isUnlimitedPlan = product?.name.includes('Advanced');

  // Show loading skeleton while price is loading
  if (!price) {
    return (
      <div className="bg-secondary border-primary flex min-h-[464px] flex-1 animate-pulse flex-col flex-nowrap rounded-2xl border px-4 py-5 xl:px-6 xl:py-7">
        <div className="bg-secondary mb-6 h-8 w-3/4 rounded" />
        <div className="mb-8">
          <div className="bg-secondary mb-4 h-4 w-1/2 rounded" />
          <div className="space-y-2">
            <div className="bg-secondary ml-8 h-3 w-1/2 rounded" />
            <div className="bg-secondary ml-8 h-3 w-1/2 rounded" />
            <div className="bg-secondary ml-8 h-3 w-1/2 rounded" />
          </div>
        </div>
        <div className="bg-secondary mb-8 mt-auto h-16 w-3/5 rounded" />
        <div className="bg-secondary h-10 w-full rounded" />
      </div>
    );
  }

  const isHighlighted = highlighted;
  const priceAmount = productStore.formatMoney(price.unit_amount);
  const isFree = priceAmount === 0;

  // Get product description based on name
  const getDescription = () => {
    if (product?.name.includes('Advanced')) {
      return 'Best value and API eligible';
    } else if (product?.name.includes('Pro')) {
      return 'More spins and pro options';
    }
    return 'Start creating motion for free';
  };

  // Extract features from product
  const features =
    product?.marketing_features?.map((f) => f.name) ||
    product?.features?.map((f) => f.name || '') ||
    [];

  return (
    <div
      className={`relative flex flex-1 flex-col flex-nowrap rounded-2xl border px-4 py-5 md:min-h-[464px] xl:px-8 xl:py-7 ${
        isHighlighted
          ? 'border-gray-30 bg-blue/10'
          : 'border-gray-30 bg-gray-15 dark:border-gray-60 dark:bg-gray-90'
      }`}
    >
      <Text
        as="h2"
        variant="web/display/lg"
        className="flex items-center gap-2"
      >
        {isHighlighted && (
          <div className="bg-blue inline-block h-4 w-4 rounded-full" />
        )}
        {product?.name}
      </Text>

      <div className="grow pt-6">
        <Text
          as="div"
          variant="web/body/medium/md"
          className="text-gray-60 dark:text-gray-45 max-w-[320px]"
        >
          {getDescription()}
          <ul className="mt-2 list-disc pl-5">
            {features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </Text>
      </div>

      <div className="mt-auto flex flex-col pt-8">
        <div className="flex items-end whitespace-nowrap">
          <Text variant="web/display/xl" lg:variant="web/display/3xl">
            ${priceAmount}
          </Text>
          {!isFree && (
            <Text
              variant="web/body/medium/lg"
              lg:variant="web/body/medium/xl"
              className="text-gray-45 dark:text-gray-60"
            >
              <span className="mx-1.5">/</span>
              {interval === 'year' ? 'yr' : 'mo'}
            </Text>
          )}
        </div>
        <Button
          className={`mt-8 ${!isHighlighted ? 'dark:bg-gray-60 bg-black' : ''} disabled:text-gray-45`}
          variant="primary"
          disabled={cta === 'Your current plan'}
          onClick={async () => {
            // Anonymous users: redirect to sign-up (with checkout redirect for paid plans)
            if (authStatus === 'anonymous') {
              if (isProPlan || isUnlimitedPlan) {
                checkoutStore.select(price);
                // Pass priceID in URL so fillPrice() can restore it after sign-up
                const checkoutParams = new URLSearchParams({
                  priceID: price.id,
                });
                navigate(
                  `/sign-up?redirectTo=/checkout?${checkoutParams.toString()}`,
                );
              } else {
                navigate('/sign-up');
              }
              return;
            }

            // Check if user is trying to switch between paid plans
            const isSwitchingBetweenPaidPlans =
              (currentPlan === 'pro' && isUnlimitedPlan) ||
              (currentPlan === 'unlimited' && isProPlan);

            if (
              !isSwitchingBetweenPaidPlans &&
              (isProPlan || isUnlimitedPlan)
            ) {
              checkoutStore.select(price);
              await checkoutStore.startCheckout();
            } else {
              const portalSession = await getStripePortalSession(
                customer?.id ?? '',
                price.id,
              );
              if (portalSession?.data) {
                window.location.href = portalSession.data.url;
              }
            }
          }}
        >
          {cta}
        </Button>
      </div>
    </div>
  );
}

function getCTA(
  authStatus: string,
  currentPlan: 'free' | 'pro' | 'unlimited',
  product?: StripeProduct | null,
) {
  if (!product) {
    return 'Loading...';
  }

  if (authStatus === 'anonymous') {
    return `Sign up for ${product.name}`;
  }

  const freeProductId = getStripeProductID('free');
  const proProductId = getStripeProductID('pro');
  const unlimitedProductId = getStripeProductID('advanced');

  // If viewing the current plan
  if (
    (currentPlan === 'free' && product.id === freeProductId) ||
    (currentPlan === 'pro' && product.id === proProductId) ||
    (currentPlan === 'unlimited' && product.id === unlimitedProductId)
  ) {
    return 'Your current plan';
  }

  // Upgrade/downgrade logic
  if (currentPlan === 'free') {
    return `Upgrade to ${product.name}`;
  } else if (currentPlan === 'pro') {
    if (product.id === unlimitedProductId) {
      return `Upgrade to ${product.name}`;
    } else {
      return `Downgrade to ${product.name}`;
    }
  } else if (currentPlan === 'unlimited') {
    return `Downgrade to ${product.name}`;
  }
  return 'Select plan';
}
